import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { ApiError } from '../../../services/api';
import {
  createBillingInvoice,
  getBillingInvoices,
  markInvoicePaid,
  reverseInvoicePayment,
  voidInvoice,
} from '../../../services/admin-billing';
import type {
  BillingInvoice,
  CreateBillingInvoiceRequest,
  MarkInvoicePaidRequest,
  ReverseInvoicePaymentRequest,
} from '../../../types/adminBilling';
import {
  DISPLAY_STATUS_LABEL,
  PAID_METHOD_LABEL,
  amountRemainingText,
  dateOnlyCell,
  formatMoney,
  instantCell,
  invoiceNumberCell,
} from './billingFormat';
import { InvoiceReminderHistory } from './InvoiceReminderHistory';
import { MarkInvoicePaidModal } from './MarkInvoicePaidModal';
import { NewInvoiceModal } from './NewInvoiceModal';
import { ReverseInvoicePaymentModal } from './ReverseInvoicePaymentModal';
import { VoidInvoiceModal } from './VoidInvoiceModal';
import { AdminPanel } from '../AdminPage';

interface BillingInvoicesPanelProps {
  organizationId: string;
  organizationName: string;
  defaultCurrency: string;
  /**
   * Stripe is the system of record for this organization, so it raises the invoices and this
   * system only mirrors them. Nothing can be added here.
   */
  isStripeManaged: boolean;
  /** Called after any change, so the page's outstanding and overdue totals follow. */
  onChanged: () => void;
}

const describe = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? err.message : fallback;

/**
 * Whether this system's own mark paid and void apply to a row.
 *
 * **Two questions, and both have to be yes.** `canSettleLocally` is the API's own rule, sent per
 * invoice: it is false on anything Stripe holds, and the same call that renders this button would
 * answer 409. `status === 'Open'` is the ordinary one — a paid, void or draft invoice has nothing
 * to settle. Neither is derivable from the other, and reading only the status is how the mirrored
 * rows came to offer a button that fails.
 */
const canSettle = (invoice: BillingInvoice) =>
  invoice.canSettleLocally && invoice.status === 'Open';

/**
 * Whether taking a payment back applies to a row.
 *
 * **Deliberately not `status === 'Paid'`.** A part payment leaves the invoice `Open` with money
 * recorded against it, and a cheque for half a bill bounces exactly as often as one for all of it.
 * The three clauses mirror what `ReverseInvoicePaymentAsync` actually refuses — Stripe's own
 * invoices, a voided one, and one nothing has been received against — so the button is offered only
 * where the call would succeed. Anything looser puts a control on screen that answers 409.
 */
const canReverse = (invoice: BillingInvoice) =>
  invoice.canSettleLocally && invoice.status !== 'Void' && (invoice.paidAmountCents ?? 0) > 0;

/** Statuses an invoice can still move out of. A paid, void or written-off one is finished. */
const LIVE_STATUSES = new Set<BillingInvoice['status']>(['Open', 'Draft']);

/**
 * Every invoice raised against this organization, newest first as the API sends them.
 *
 * **The status shown is `displayStatus`**, which the API derives. The client never compares
 * `dueOn` to today: the server owns where "open" becomes "overdue", and a second opinion here is
 * how this table comes to disagree with the reminder the customer has already received.
 *
 * **A mirrored invoice is a link out, not a row to act on.** Stripe holds it, so the actions are
 * its hosted page and its PDF, and the row says in one line why mark paid and void are not offered
 * rather than offering them and letting an admin discover the refusal.
 *
 * A row's reminder history is loaded only when it is opened. Most rows are never opened, and
 * fetching a history per invoice on mount would be a request per row for a table nobody asked a
 * question of.
 */
export const BillingInvoicesPanel = ({
  organizationId,
  organizationName,
  defaultCurrency,
  isStripeManaged,
  onChanged,
}: BillingInvoicesPanelProps) => {
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [payingInvoice, setPayingInvoice] = useState<BillingInvoice | null>(null);
  const [voidingInvoice, setVoidingInvoice] = useState<BillingInvoice | null>(null);
  const [reversingInvoice, setReversingInvoice] = useState<BillingInvoice | null>(null);
  /*
    The same failure the dialogs show, kept where closing a dialog cannot take it away. A write can
    outlive its modal: the admin confirms, nothing appears to happen, they press Escape, and only
    then does the call reject. `modalError` is a prop of a component that is no longer rendered, so
    the money did not move and the screen said nothing at all. The Stripe panel already keeps a
    panel-level error for this reason.
  */
  const [actionError, setActionError] = useState<string | null>(null);
  /*
    WHICH action is in flight, not merely that one is.

    A single shared boolean was wrong the moment a dialog could be closed mid-request, which is the
    case the panel-level error below exists for. With mark-paid still running and its dialog shut,
    opening the reverse dialog showed it as already saving and swallowed the confirm; worse, the
    first request's `finally` cleared the flag while the reversal was still in flight, un-busying
    its confirm button. Two presses then sent two partial reversals, and the API accepts both -
    `held <= 0` only refuses once everything received has been taken back.
  */
  const [savingAction, setSavingAction] = useState<'pay' | 'void' | 'reverse' | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setLoadError(null);
      try {
        const loaded = await getBillingInvoices(organizationId, signal);
        if (signal?.aborted) return;
        setInvoices(loaded);
      } catch (err: unknown) {
        if (signal?.aborted) return;
        setLoadError(describe(err, 'Could not load the invoices.'));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [organizationId]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const handleCreate = async (request: CreateBillingInvoiceRequest) => {
    setCreating(true);
    setCreateError(null);
    // Cleared here too: a stale failure from a previous action must not sit above a table that has
    // just successfully changed.
    setActionError(null);
    try {
      await createBillingInvoice(organizationId, request);
      setCreateOpen(false);
      await load();
      onChanged();
    } catch (err: unknown) {
      setCreateError(describe(err, 'Could not create the invoice.'));
    } finally {
      setCreating(false);
    }
  };

  const handleMarkPaid = async (request: MarkInvoicePaidRequest) => {
    if (!payingInvoice || savingAction) return;
    setSavingAction('pay');
    setModalError(null);
    setActionError(null);
    try {
      await markInvoicePaid(payingInvoice.id, request);
      setPayingInvoice(null);
      // Both, and in this order: the table is the thing that changed, and the summary above it
      // carries the outstanding and overdue totals this payment just moved.
      await load();
      onChanged();
    } catch (err: unknown) {
      // A 409 is the API refusing because the invoice is no longer open. Its message says which,
      // and it is the one worth showing. Written to both: the dialog if it is still open, the panel
      // if the admin has already closed it.
      const message = describe(err, 'Could not mark the invoice paid.');
      setModalError(message);
      setActionError(message);
    } finally {
      setSavingAction(null);
    }
  };

  const handleVoid = async (reason: string) => {
    if (!voidingInvoice || savingAction) return;
    setSavingAction('void');
    setModalError(null);
    setActionError(null);
    try {
      await voidInvoice(voidingInvoice.id, reason);
      setVoidingInvoice(null);
      await load();
      onChanged();
    } catch (err: unknown) {
      const message = describe(err, 'Could not void the invoice.');
      setModalError(message);
      setActionError(message);
    } finally {
      setSavingAction(null);
    }
  };

  const handleReverse = async (request: ReverseInvoicePaymentRequest) => {
    if (!reversingInvoice || savingAction) return;
    setSavingAction('reverse');
    setModalError(null);
    setActionError(null);
    try {
      await reverseInvoicePayment(reversingInvoice.id, request);
      setReversingInvoice(null);
      // Same pair as mark-paid, and for the same reason: reversing money puts it back into the
      // outstanding and overdue totals the summary above this table carries.
      await load();
      onChanged();
    } catch (err: unknown) {
      const message = describe(err, 'Could not reverse the payment.');
      setModalError(message);
      setActionError(message);
    } finally {
      setSavingAction(null);
    }
  };

  return (
    <AdminPanel
      title="Invoices"
      titleId="billing-invoices-title"
      description={
        isStripeManaged
          ? 'Raised by Stripe and mirrored here. Open a row to see every reminder that was sent for it.'
          : 'Raised by the plan, or by hand for anything outside it. Open a row to see every reminder that was attempted for it.'
      }
      /*
        No "new invoice" button for a Stripe-managed organization: the API refuses to raise one,
        because an invoice created here would not exist at Stripe and nobody would ever be asked
        to pay it. The sentence says where to raise it instead.
      */
      actions={
        isStripeManaged ? (
          <p className="billing-panel__note" data-testid="billing-invoices-stripe-managed">
            Stripe raises this organization&apos;s invoices. Raise one in the Stripe dashboard and it
            appears here.
          </p>
        ) : (
          <Button
            variant="secondary"
            onClick={() => {
              setCreateError(null);
              setCreateOpen(true);
            }}
            data-testid="billing-invoice-new"
          >
            New invoice
          </Button>
        )
      }
    >

      {loadError && (
        <p className="admin-error" role="alert">
          {loadError}
        </p>
      )}

      {/*
        Shown whether or not the dialog that started the write is still open. Dismissing a dialog
        mid-request is a reasonable thing to do when nothing seems to be happening, and without this
        the rejection that follows lands on an unmounted component and the admin is left looking at
        an unchanged table with no reason to doubt it.
      */}
      {actionError && (
        <p className="admin-error" role="alert">
          {actionError}
        </p>
      )}

      {loading ? (
        <p className="admin-state">Loading the invoices...</p>
      ) : invoices.length === 0 ? (
        <p className="admin-state" data-testid="billing-invoices-empty">
          {isStripeManaged
            ? 'No invoices yet. Anything Stripe already holds for this customer arrives once you import it from the Stripe panel below.'
            : 'No invoices yet. The first one appears when the plan reaches its first invoice date, or you can raise one by hand.'}
        </p>
      ) : (
        <div className="billing-panel__table-container">
          <table className="billing-panel__table">
            <thead>
              <tr>
                <th scope="col">Invoice</th>
                <th scope="col">Issued</th>
                <th scope="col">Due</th>
                <th scope="col">Amount</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const expanded = expandedId === invoice.id;
                const historyId = `billing-invoice-history-${invoice.id}`;
                const mirrored = invoice.source === 'Stripe';
                const remaining = amountRemainingText(invoice);
                return [
                  <tr key={invoice.id} data-testid={`billing-invoice-${invoice.id}`}>
                    <td>
                      <span className="billing-panel__contact-main">
                        <span
                          className="billing-panel__contact-email"
                          data-testid={`billing-invoice-number-${invoice.id}`}
                        >
                          {invoiceNumberCell(invoice)}
                        </span>
                        {mirrored && (
                          <span
                            className="billing-panel__contact-meta"
                            data-testid={`billing-invoice-source-${invoice.id}`}
                          >
                            Held by Stripe
                          </span>
                        )}
                        {(invoice.periodStart || invoice.periodEnd) && (
                          <span className="billing-panel__contact-meta">
                            {dateOnlyCell(invoice.periodStart)} to {dateOnlyCell(invoice.periodEnd)}
                          </span>
                        )}
                        {invoice.paidAt && (
                          <span className="billing-panel__contact-meta">
                            Paid {instantCell(invoice.paidAt)}
                            {invoice.paidAmountCents !== null
                              ? ` - ${formatMoney(invoice.paidAmountCents, invoice.currency)}`
                              : ''}
                            {invoice.paymentMethod
                              ? ` - ${PAID_METHOD_LABEL[invoice.paymentMethod]}`
                              : ''}
                          </span>
                        )}
                        {invoice.voidReason && (
                          <span className="billing-panel__contact-meta">
                            Voided: {invoice.voidReason}
                          </span>
                        )}
                      </span>
                    </td>
                    <td>{dateOnlyCell(invoice.issuedOn)}</td>
                    <td>{dateOnlyCell(invoice.dueOn)}</td>
                    <td>
                      <span className="billing-panel__contact-main">
                        <span>{formatMoney(invoice.amountCents, invoice.currency)}</span>
                        {/*
                          Only when Stripe has taken part of it. A remainder equal to the amount is
                          an untouched invoice and a remainder of nothing is a settled one, and the
                          amount and the status already say both of those.
                        */}
                        {remaining && (
                          <span
                            className="billing-panel__contact-meta"
                            data-testid={`billing-invoice-remaining-${invoice.id}`}
                          >
                            {remaining}
                          </span>
                        )}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`billing-panel__status-tag billing-panel__status-tag--${invoice.displayStatus}`}
                        data-testid={`billing-invoice-status-${invoice.id}`}
                      >
                        {DISPLAY_STATUS_LABEL[invoice.displayStatus]}
                        {invoice.displayStatus === 'overdue' && invoice.daysOverdue > 0
                          ? ` ${invoice.daysOverdue}d`
                          : ''}
                      </span>
                    </td>
                    <td>
                      <span className="billing-panel__row-actions">
                        {canSettle(invoice) && (
                          <>
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setModalError(null);
                                setPayingInvoice(invoice);
                              }}
                              data-testid={`billing-invoice-pay-${invoice.id}`}
                            >
                              Mark paid
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setModalError(null);
                                setVoidingInvoice(invoice);
                              }}
                              data-testid={`billing-invoice-void-${invoice.id}`}
                            >
                              Void
                            </Button>
                          </>
                        )}
                        {canReverse(invoice) && (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setModalError(null);
                              setActionError(null);
                              setReversingInvoice(invoice);
                            }}
                            data-testid={`billing-invoice-reverse-${invoice.id}`}
                          >
                            Reverse payment
                          </Button>
                        )}
                        {invoice.hostedInvoiceUrl && (
                          <a
                            className="billing-panel__external-link"
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`billing-invoice-hosted-${invoice.id}`}
                          >
                            View at Stripe
                          </a>
                        )}
                        {invoice.invoicePdfUrl && (
                          <a
                            className="billing-panel__external-link"
                            href={invoice.invoicePdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`billing-invoice-pdf-${invoice.id}`}
                          >
                            PDF
                          </a>
                        )}
                        <Button
                          variant="secondary"
                          onClick={() => setExpandedId(expanded ? null : invoice.id)}
                          aria-expanded={expanded}
                          aria-controls={historyId}
                          data-testid={`billing-invoice-reminders-${invoice.id}`}
                        >
                          {expanded ? 'Hide reminders' : 'Reminders'}
                        </Button>
                      </span>
                      {/*
                        One line, only where an admin would otherwise go looking for the buttons: a
                        mirrored invoice that is still live. A paid, void or written-off one has
                        nothing to settle whoever holds it, so saying so there would be noise on
                        every closed row.
                      */}
                      {mirrored && !canSettle(invoice) && LIVE_STATUSES.has(invoice.status) && (
                        <span
                          className="billing-panel__contact-meta"
                          data-testid={`billing-invoice-locked-${invoice.id}`}
                        >
                          {invoice.status === 'Draft'
                            ? 'Stripe has not issued this invoice yet. Finish it at Stripe.'
                            : 'Stripe holds this invoice. Mark it paid or void it at Stripe.'}
                        </span>
                      )}
                    </td>
                  </tr>,
                  expanded ? (
                    <tr key={`${invoice.id}-history`} id={historyId}>
                      <td colSpan={6}>
                        <InvoiceReminderHistory invoiceId={invoice.id} />
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      <NewInvoiceModal
        isOpen={createOpen}
        organizationName={organizationName}
        defaultCurrency={defaultCurrency}
        saving={creating}
        error={createError}
        onCreate={handleCreate}
        onClose={() => setCreateOpen(false)}
      />

      <MarkInvoicePaidModal
        invoice={payingInvoice}
        saving={savingAction === 'pay'}
        error={modalError}
        onConfirm={handleMarkPaid}
        onClose={() => setPayingInvoice(null)}
      />

      <VoidInvoiceModal
        invoice={voidingInvoice}
        saving={savingAction === 'void'}
        error={modalError}
        onConfirm={handleVoid}
        onClose={() => setVoidingInvoice(null)}
      />

      <ReverseInvoicePaymentModal
        invoice={reversingInvoice}
        saving={savingAction === 'reverse'}
        error={modalError}
        onConfirm={handleReverse}
        onClose={() => setReversingInvoice(null)}
      />
    </AdminPanel>
  );
};
